import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  QueryOptions,
  concat,
} from '@apollo/client/core';

import { Sha256 } from '@aws-crypto/sha256-browser';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import config from '@/config';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { requireJwt } from '@/services/auth';
import logger from '@/services/logger';
import { errorDetails } from '@/services/logger/errorDetails';
import { setContext } from '@apollo/client/link/context';
import { useStore } from '@/store';

const { region, hosts, guestHost } = config.services.freedomQl;

// USING COGNITO JWT

const httpLink = new HttpLink({
  uri: hosts[0],
});

const authMiddleware = setContext(async (_operation) => {
  const token = await requireJwt();
  return {
    headers: {
      authorization: `Bearer ${token}`,
    },
  };
});

export const client = new ApolloClient({
  link: concat(authMiddleware, httpLink),
  cache: new InMemoryCache(),
});

// USING IAM AUTH

const creds = fromCognitoIdentityPool({
  clientConfig: { region },
  identityPoolId:
    config.services.aws.awsConfig.Auth?.Cognito.identityPoolId ?? '',
});

const signer = new SignatureV4({
  service: 'execute-api',
  region,
  credentials: creds,
  sha256: Sha256,
});

const iamLink = new HttpLink({
  uri: guestHost,
  fetch: async (
    input: RequestInfo | URL,
    options: RequestInit = {},
  ): Promise<Response> => {
    const url =
      typeof input === 'string'
        ? new URL(input)
        : input instanceof URL
          ? input
          : new URL(input.url);

    const request = {
      method: options.method ?? 'POST',
      protocol: url.protocol,
      hostname: url.hostname,
      path: url.pathname,
      headers: {
        host: url.host,
        ...(options.headers as Record<string, string>),
      },
      body: options.body,
    };

    const signed = await signer.sign(request);

    return fetch(input, {
      ...options,
      method: request.method,
      headers: signed.headers,
    });
  },
});

export const guestClient = new ApolloClient({
  link: iamLink,
  cache: new InMemoryCache(),
});

// QUERY

export const graphQlQuery = async (queryName: string, query: QueryOptions) => {
  const activeClient = useStore().isGuestSession ? guestClient : client;

  try {
    const { data, error } = await activeClient.query(query);

    if (error) {
      logger.error(`freedomQL: ${queryName}`, error);
      throw new Error(error.message);
    }

    logger.debug(`freedomQL: ${queryName}`, data);

    return data;
  } catch (err: any) {
    logger.error(`freedomQL: ${queryName} Exception`, errorDetails(err));
  }
};
